/**
 * Helper form component for rendering email inputs. This handles its own state
 * and validation. Returns the actual typed value to parent component to maintain
 * focus, and notifies parent of validation state via onValidationChange callback.
 *
 */

// import primary libraries
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const EmailInput = ({
  change
  , disabled
  , helpText
  , label
  , name
  , onValidationChange
  , placeholder
  , required
  , value = ''
  , ...inputProps
}) => {
  const [email, setEmail] = useState(value);
  const [error, setError] = useState(null);

  // Sync internal state when value prop changes
  useEffect(() => {
    setEmail(value);
  }, [value]);

  const onChange = e => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    const emailIsValid = validateEmail(newEmail);

    // Always send the actual typed value to maintain input focus
    change({
      target: {
        name: name
        , value: newEmail
      }
    });

    // Update validation state
    if(emailIsValid) {
      setError(null);
      onValidationChange?.(true);
    } else {
      setError("Please enter a valid email address");
      onValidationChange?.(false);
    }
  }

  return (
    <div className="relative z-0 w-full mb-4 text-left lg:w-auto">
      { label ? (
        <label
          htmlFor={name}
          className="px-2 pt-1 text-xs absolute top-0 text-neutral-sec bg-transparent z-10"
        >
          {label} <sup className="text-error">{required ? '*' : null}</sup>
        </label>
        )
        :
        null
      }
      <input
        disabled={disabled}
        // default the id to the name so a sibling `<label htmlFor={name}>` actually
        // names the field and focuses it on click. callers can still pass an explicit id.
        id={name}
        name={name}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        type="email"
        value={email}
        className={`px-2 text-base ${label ? 'pt-5 pb-1' : 'pt-3 pb-3'} block w-full mt-0`}
        {...inputProps}
      />
      { error && <div className="text-xs text-error">{error}</div> }
      {helpText && <small className="text-xs text-neutral-sec"><em>{helpText}</em></small>}
    </div>
  )
}

EmailInput.propTypes = {
  change: PropTypes.func.isRequired
  , disabled: PropTypes.bool
  , helpText: PropTypes.any
  , label: PropTypes.string
  , name: PropTypes.string.isRequired
  , onValidationChange: PropTypes.func
  , placeholder: PropTypes.string
  , required: PropTypes.bool
  , value: PropTypes.string.isRequired
}

EmailInput.defaultProps = {
  disabled: false
  , helpText: null
  , label: ''
  , placeholder: ''
  , required: false
}

// Email validation function - exported so parent components can validate initial values
export const validateEmail = email => {
  // Checks for ____@____.__
  const validEmailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return validEmailRegex.test(email);
}

export default EmailInput;